import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, FileText, Calendar, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ResumeLibraryService, SavedResume } from "@/services/resumeLibraryService";

export default function SavedResumeDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [resume, setResume] = useState<SavedResume | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (id) {
      loadResume();
    }
  }, [id]);

  const loadResume = async () => {
    if (!id) return;
    
    try {
      setIsLoading(true);
      const resumes = await ResumeLibraryService.getSavedResumes();
      const foundResume = resumes.find(r => r.id === id);
      
      if (foundResume) {
        setResume(foundResume);
      } else {
        toast({
          title: "Resume Not Found",
          description: "The requested resume could not be found.",
          variant: "destructive"
        });
        navigate("/");
      }
    } catch (error) {
      console.error('Error loading resume:', error);
      toast({
        title: "Error",
        description: "Failed to load resume details",
        variant: "destructive"
      });
      navigate("/");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!resume) return;
    
    if (!confirm(`Are you sure you want to delete "${resume.title}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setIsDeleting(true);
      await ResumeLibraryService.deleteResume(resume.id);
      
      toast({
        title: "Resume Deleted",
        description: "Resume has been removed from your library."
      });
      
      // Go back one step after deletion
      navigate(-1);
      
    } catch (error) {
      console.error('Error deleting resume:', error);
      toast({
        title: "Delete Failed",
        description: error instanceof Error ? error.message : "Failed to delete resume",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBack = () => {
    // Go back one step in browser history to return to the library
    navigate(-1);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading resume details...</p>
        </div>
      </div>
    );
  }

  if (!resume) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-96">
          <CardHeader>
            <CardTitle>Resume Not Found</CardTitle>
            <CardDescription>The requested resume could not be found.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={handleBack}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Resume Library
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center mb-8">
          <Button variant="ghost" onClick={handleBack} className="mr-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Resume Library
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold">{resume.title}</h1>
            <p className="text-muted-foreground">Saved Resume Details</p>
          </div>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <div className="w-4 h-4 border border-current border-t-transparent rounded-full animate-spin mr-2" />
            ) : (
              <Trash2 className="w-4 h-4 mr-2" />
            )}
            Delete Resume
          </Button>
        </div>

        <div className="max-w-4xl mx-auto space-y-6">
          {/* Resume Information Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Resume Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Basic Info */}
              <div>
                <h4 className="font-semibold mb-3">Basic Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Title:</span>
                    <p className="font-medium">{resume.title}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Date Created:</span>
                    <p className="font-medium flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(resume.dateCreated)}
                    </p>
                  </div>
                  {resume.fileName && (
                    <div>
                      <span className="text-muted-foreground">Original File:</span>
                      <p className="font-medium">{resume.fileName}</p>
                    </div>
                  )}
                  {resume.fileType && (
                    <div>
                      <span className="text-muted-foreground">File Type:</span>
                      <p className="font-medium">{resume.fileType}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Job Targeting Info */}
              {(resume.jobTitle || resume.companyName || resume.matchScore !== undefined) && (
                <div>
                  <h4 className="font-semibold mb-3">Job Targeting</h4>
                  <div className="flex flex-wrap gap-2">
                    {resume.matchScore !== undefined && (
                      <Badge variant="outline" className="text-sm px-3 py-1">
                        Match Score: {resume.matchScore}%
                      </Badge>
                    )}
                    {resume.jobTitle && (
                      <Badge variant="outline" className="text-sm px-3 py-1">
                        Job: {resume.jobTitle}
                      </Badge>
                    )}
                    {resume.companyName && (
                      <Badge variant="outline" className="text-sm px-3 py-1">
                        Company: {resume.companyName}
                      </Badge>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Resume Content Card */}
          <Card>
            <CardHeader>
              <CardTitle>Resume Content</CardTitle>
              <CardDescription>
                Full text content of your resume
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-muted/50 rounded-lg p-6 max-h-96 overflow-y-auto">
                <pre className="text-sm whitespace-pre-wrap font-sans">{resume.content}</pre>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}